import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Star, Plus, X, Loader2, AlertCircle, MapPin, Heart } from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import { Input } from "@tabipla/ui/components/input";
import { Textarea } from "@tabipla/ui/components/textarea";
import { toast } from "sonner";
import { MekkeLayout } from "../layouts/MekkeLayout";
import {
  useSpot,
  useCreateSpot,
  useUpdateSpot,
  useAddSpotImage,
  useRemoveSpotImage,
} from "../../application/hooks/useSpots";
import { spotCategories } from "../../config/categories";
import { cn } from "@tabipla/ui/lib/utils";
import { PlaceAutocomplete, type PlaceResult } from "../components/PlaceAutocomplete";
import { SpotImageUploader } from "../components/spots/SpotImageUploader";
import type { MekkeSpotStatus, MekkeSpotCategory } from "@tabipla/shared";

export function SpotFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Fetch existing spot data if editing
  const { data: existingSpot, isLoading: isLoadingSpot } = useSpot(id);
  const createMutation = useCreateSpot();
  const updateMutation = useUpdateSpot(id || "");

  // Image upload hooks (only used in edit mode)
  const addImageMutation = useAddSpotImage(id || "");
  const removeImageMutation = useRemoveSpotImage(id || "");

  // Form state
  const [name, setName] = useState("");
  const nameRef = useRef(name);
  nameRef.current = name;
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [category, setCategory] = useState<MekkeSpotCategory>("other");
  const [status, setStatus] = useState<MekkeSpotStatus>("visited");
  const [rating, setRating] = useState<number | null>(null);
  const [visitedAt, setVisitedAt] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");

  // Load existing spot data when editing
  useEffect(() => {
    if (existingSpot) {
      setName(existingSpot.name);
      setDescription(existingSpot.description || "");
      setAddress(existingSpot.address || "");
      setLatitude(existingSpot.latitude ?? null);
      setLongitude(existingSpot.longitude ?? null);
      setPlaceId(existingSpot.placeId ?? null);
      setCategory(existingSpot.category);
      setStatus(existingSpot.status);
      setRating(existingSpot.rating);
      setVisitedAt(
        existingSpot.visitedAt ? new Date(existingSpot.visitedAt).toISOString().split("T")[0] : ""
      );
      setTags(existingSpot.tags);
    }
  }, [existingSpot]);

  // Handle place selection from autocomplete
  const handlePlaceChange = useCallback((place: PlaceResult | null) => {
    if (place) {
      setAddress(place.formattedAddress || "");
      setLatitude(place.latitude || null);
      setLongitude(place.longitude || null);
      setPlaceId(place.placeId || null);
      // If name is empty, use place name as suggestion
      if (!nameRef.current && place.name) {
        setName(place.name);
      }
    } else {
      setAddress("");
      setLatitude(null);
      setLongitude(null);
      setPlaceId(null);
    }
  }, []);

  const handleAddTag = () => {
    const trimmedTag = newTag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(t("spots.nameRequired"));
      return;
    }

    const spotData = {
      name: name.trim(),
      description: description.trim() || undefined,
      address: address.trim() || undefined,
      latitude: latitude ?? undefined,
      longitude: longitude ?? undefined,
      placeId: placeId ?? undefined,
      category,
      status,
      rating: rating || undefined,
      visitedAt: visitedAt ? new Date(visitedAt).toISOString() : undefined,
      tags: tags.length > 0 ? tags : undefined,
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(spotData);
        toast.success(t("spots.updated"));
      } else {
        const result = await createMutation.mutateAsync(spotData);
        toast.success(t("spots.created"));
        navigate(`/spots/${result?.id}`);
        return;
      }
      navigate(-1);
    } catch {
      toast.error(t("common.error"));
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEditing && isLoadingSpot) {
    return (
      <MekkeLayout hideNav>
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MekkeLayout>
    );
  }

  if (isEditing && !existingSpot && !isLoadingSpot) {
    return (
      <MekkeLayout hideNav>
        <div className="flex min-h-screen flex-col items-center justify-center px-4">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-10 w-10 text-destructive" />
          </div>
          <h2 className="mb-2 text-lg font-semibold">{t("common.error")}</h2>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t("common.unknownError")}
          </p>
          <Button variant="outline" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")}
          </Button>
        </div>
      </MekkeLayout>
    );
  }

  return (
    <MekkeLayout hideNav>
      <div className="min-h-screen pb-24">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto flex max-w-4xl items-center px-4 py-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="flex-1 text-center text-lg font-semibold">
              {isEditing ? t("spots.edit") : t("spots.add")}
            </h1>
            <div className="w-10" /> {/* Spacer for balance */}
          </div>
        </header>

        {/* Form */}
        <div className="mx-auto max-w-4xl px-4 py-6">
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t("spots.name")} <span className="text-destructive">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("spots.name")}
              />
            </div>

            {/* Status */}
            <div>
              <label className="mb-2 block text-sm font-medium">{t("spots.status")}</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStatus("visited")}
                  className={cn(
                    "flex flex-1 flex-col items-center gap-2 rounded-xl border-2 py-4 text-sm font-medium transition-all",
                    status === "visited"
                      ? "shadow-sm"
                      : "border-border bg-card text-muted-foreground hover:border-indigo-200 hover:bg-indigo-50/50"
                  )}
                  style={
                    status === "visited"
                      ? {
                          borderColor: "#6366f1",
                          backgroundColor: "#e0e7ff",
                          color: "#4338ca",
                        }
                      : undefined
                  }
                >
                  <MapPin
                    className="h-6 w-6"
                    style={{ color: status === "visited" ? "#4f46e5" : undefined }}
                  />
                  {t("spots.visited")}
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("want_to_go")}
                  className={cn(
                    "flex flex-1 flex-col items-center gap-2 rounded-xl border-2 py-4 text-sm font-medium transition-all",
                    status === "want_to_go"
                      ? "shadow-sm"
                      : "border-border bg-card text-muted-foreground hover:border-amber-200 hover:bg-amber-50/50"
                  )}
                  style={
                    status === "want_to_go"
                      ? {
                          borderColor: "#f59e0b",
                          backgroundColor: "#fef3c7",
                          color: "#b45309",
                        }
                      : undefined
                  }
                >
                  <Heart
                    className="h-6 w-6"
                    style={{ color: status === "want_to_go" ? "#d97706" : undefined }}
                  />
                  {t("spots.wantToGo")}
                </button>
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="mb-2 block text-sm font-medium">{t("spots.category")}</label>
              <div className="grid grid-cols-4 gap-2">
                {spotCategories.map((cat) => {
                  const Icon = cat.icon;
                  const isSelected = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      className={cn(
                        "flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-xs transition-all",
                        isSelected ? "shadow-sm" : "border-border hover:border-gray-300"
                      )}
                      style={{
                        borderColor: isSelected ? cat.color : undefined,
                        backgroundColor: isSelected ? `${cat.color}15` : undefined,
                      }}
                    >
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-full"
                        style={{ backgroundColor: `${cat.color}20` }}
                      >
                        <Icon className="h-5 w-5" style={{ color: cat.color }} />
                      </div>
                      <span
                        className="font-medium"
                        style={{ color: isSelected ? cat.color : undefined }}
                      >
                        {t(cat.labelKey)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="mb-2 block text-sm font-medium">{t("spots.address")}</label>
              <PlaceAutocomplete
                value={address}
                onChange={handlePlaceChange}
                placeholder={t("spots.searchPlace")}
              />
            </div>

            {/* Rating */}
            <div>
              <label className="mb-2 block text-sm font-medium">{t("spots.rating")}</label>
              <div className="flex items-center gap-2">
                <div className="flex gap-1 rounded-xl border-2 border-border bg-card p-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(rating === star ? null : star)}
                      className="rounded-lg p-1 transition-all hover:scale-110"
                    >
                      <Star
                        className={cn(
                          "h-8 w-8 transition-colors",
                          rating && star <= rating
                            ? "fill-amber-400 text-amber-400"
                            : "text-gray-300 hover:text-amber-200"
                        )}
                      />
                    </button>
                  ))}
                </div>
                {rating && (
                  <button
                    type="button"
                    onClick={() => setRating(null)}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                  >
                    {t("common.clear")}
                  </button>
                )}
              </div>
            </div>

            {/* Visit Date */}
            {status === "visited" && (
              <div>
                <label className="mb-2 block text-sm font-medium">{t("spots.visitedAt")}</label>
                <Input
                  type="date"
                  value={visitedAt}
                  onChange={(e) => setVisitedAt(e.target.value)}
                />
              </div>
            )}

            {/* Description */}
            <div>
              <label className="mb-2 block text-sm font-medium">{t("spots.description")}</label>
              <Textarea
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setDescription(e.target.value)
                }
                placeholder={t("spots.description")}
                rows={4}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="mb-2 block text-sm font-medium">{t("spots.tags")}</label>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder={t("spots.addTag")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleAddTag}
                  disabled={!newTag.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="rounded-full p-0.5 hover:bg-background"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Photos (Edit mode only) */}
            {isEditing && existingSpot && (
              <SpotImageUploader
                images={existingSpot.images}
                onUpload={addImageMutation.mutateAsync}
                onRemove={removeImageMutation.mutateAsync}
                isUploading={addImageMutation.isPending}
                disabled={isSubmitting}
              />
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto max-w-4xl px-4 py-4">
            {isEditing ? (
              <div className="flex gap-3">
                <Button className="flex-1" size="lg" variant="outline" onClick={() => navigate(-1)}>
                  {t("common.close")}
                </Button>
                <Button
                  className="flex-1"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !name.trim()}
                >
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("common.save")}
                </Button>
              </div>
            ) : (
              <Button
                className="w-full"
                size="lg"
                onClick={handleSubmit}
                disabled={isSubmitting || !name.trim()}
              >
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("common.save")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </MekkeLayout>
  );
}
